import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Tag, Space, Tabs, Typography, InputNumber, Row, Col, Switch, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, PercentageOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { linkRequestsAPI } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

const { Text } = Typography;

interface Customer {
  id: number;
  isApproved: boolean;
  user?: {
    id: number;
    username?: string;
    fullName: string;
    email: string;
    phone: string;
    companyName: string;
    inn: string;
    approval?: {
      responsiblePosition?: string | null;
      responsibleFullName?: string | null;
      responsiblePhone?: string | null;
    } | null;
  };
}

interface LinkRequest {
  id: number;
  customerId: number;
  supplierId: number;
  status: string;
  createdAt: string;
  customer?: Customer; // Может быть undefined
  initiatorType: string; // "customer" или "supplier"
  message?: string;
  discountPercent?: number;
  discountUpdatedAt?: string;
  allowEmergencyOrders?: boolean;
  emergencyEnabledAt?: string | null;
  emergencyExpiresAt?: string | null;
}

const MyCustomers: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [myRequests, setMyRequests] = useState<LinkRequest[]>([]);
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [tab, setTab] = useState<'approved' | 'pending' | 'rejected' | 'archived'>('approved');
  const [form] = Form.useForm();
  const [discountModalVisible, setDiscountModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<LinkRequest | null>(null);
  const [discountForm] = Form.useForm();
  const [emergencyPermissionLoading, setEmergencyPermissionLoading] = useState<number | null>(null);

  useEffect(() => {
    loadMyRequests();
  }, []);

  // Вспомогательная функция для безопасного получения данных заказчика
  const getCustomerData = (customer: Customer | undefined) => {
    if (!customer) {
      return {
        companyName: 'Не указано',
        inn: 'Не указан',
        fullName: 'Не указано',
        phone: 'Не указан',
        email: 'Не указан'
      };
    }
    
    const userData = customer.user;
    const approval = customer.user?.approval;
    return {
      companyName: userData?.companyName || 'Не указано',
      inn: userData?.inn || 'Не указан',
      fullName:
        approval?.responsibleFullName ||
        userData?.fullName ||
        customer.user?.username ||
        userData?.companyName ||
        'Не указано',
      phone: approval?.responsiblePhone || userData?.phone || 'Не указан',
      position: approval?.responsiblePosition || 'Должность не указана',
      email: userData?.email || 'Не указан'
    };
  };

  const loadMyRequests = async () => {
    setLoading(true);
    try {
      const data = await linkRequestsAPI.getSupplierRequests();
      setMyRequests(data);
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (values: { inn?: string; companyName?: string }) => {
    setSearching(true);
    try {
      console.log('🔍 Searching with values:', values);
      console.log('🔍 Values type:', typeof values);
      console.log('🔍 Values keys:', Object.keys(values));
      console.log('🔍 INN value:', values.inn, 'Type:', typeof values.inn);
      console.log('🔍 Company name value:', values.companyName, 'Type:', typeof values.companyName);
      
      // Check if values are empty or undefined
      if (!values.inn && !values.companyName) {
        console.log('❌ Both INN and company name are empty');
        message.warning('Введите ИНН или название компании для поиска');
        return;
      }
      
      const data = await linkRequestsAPI.searchCustomers(values);
      console.log('📊 Search results:', data);
      console.log('📊 Search results type:', typeof data);
      console.log('📊 Search results is array:', Array.isArray(data));
      console.log('📊 Search results length:', data?.length);
      setSearchResults(data);
      
      if (data.length === 0) {
        message.info('Заказчики не найдены');
      } else {
        message.success(`Найдено заказчиков: ${data.length}`);
      }
    } catch (error: any) {
      console.error('❌ Search error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      message.error(error?.response?.data?.error || 'Ошибка поиска');
    } finally {
      setSearching(false);
    }
  };

  const handleSendRequest = async (customerId: number) => {
    try {
      const values = form.getFieldsValue();
      await linkRequestsAPI.createSupplierToCustomerRequest({ customerId, message: values.message });
      message.success('Предложение отправлено заказчику');
      setSearchModalVisible(false);
      form.resetFields();
      setSearchResults([]);
      loadMyRequests();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка отправки предложения');
    }
  };

  const handleApproveRequest = async (requestId: number) => {
    // Находим заявку для отображения деталей
    const request = myRequests.find(r => r.id === requestId);
    if (!request?.customer) {
      message.error('Информация о заказчике недоступна');
      return;
    }
    
    const customerData = getCustomerData(request.customer);
    const customerName = customerData.companyName;
    const customerContact = customerData.fullName;
    
    Modal.confirm({
      title: 'Подтверждение одобрения заявки',
      content: (
        <div>
          <p><strong>⚠️ Внимание!</strong> Вы собираетесь одобрить заявку на сотрудничество.</p>
          <div style={{ 
            background: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px', 
            padding: '12px', 
            margin: '12px 0' 
          }}>
            <p><strong>Заказчик:</strong> {customerName}</p>
            <p><strong>Контакт:</strong> {customerContact}</p>
            {request?.message && <p><strong>Сообщение:</strong> {request.message}</p>}
          </div>
          <p>После одобрения заказчик получит email уведомление и сможет видеть ваше меню.</p>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            Это действие нельзя отменить!
          </p>
        </div>
      ),
      okText: 'Одобрить заявку',
      cancelText: 'Отмена',
      okType: 'primary',
      width: 500,
      onOk: async () => {
        try {
          await linkRequestsAPI.approveRequest(requestId);
          message.success('✅ Заявка одобрена! Заказчик получил email уведомление.');
          loadMyRequests();
        } catch (error: any) {
          message.error(error?.response?.data?.error || 'Ошибка одобрения заявки');
        }
      }
    });
  };

  const handleRejectRequest = async (requestId: number) => {
    // Находим заявку для отображения деталей
    const request = myRequests.find(r => r.id === requestId);
    if (!request?.customer) {
      message.error('Информация о заказчике недоступна');
      return;
    }
    
    const customerData = getCustomerData(request.customer);
    const customerName = customerData.companyName;
    const customerContact = customerData.fullName;
    
    Modal.confirm({
      title: 'Подтверждение отклонения заявки',
      content: (
        <div>
          <p><strong>⚠️ Внимание!</strong> Вы собираетесь отклонить заявку на сотрудничество.</p>
          <div style={{ 
            background: '#fff2f0', 
            border: '1px solid #ffccc7', 
            borderRadius: '6px', 
            padding: '12px', 
            margin: '12px 0' 
          }}>
            <p><strong>Заказчик:</strong> {customerName}</p>
            <p><strong>Контакт:</strong> {customerContact}</p>
            {request?.message && <p><strong>Сообщение:</strong> {request.message}</p>}
          </div>
          <p>После отклонения заказчик получит email уведомление с объяснением причины.</p>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            Это действие нельзя отменить!
          </p>
        </div>
      ),
      okText: 'Отклонить заявку',
      cancelText: 'Отмена',
      okType: 'danger',
      width: 500,
      onOk: async () => {
        try {
          await linkRequestsAPI.rejectRequest(requestId);
          message.success('❌ Заявка отклонена! Заказчик получил email уведомление.');
          loadMyRequests();
        } catch (error: any) {
          message.error(error?.response?.data?.error || 'Ошибка отклонения заявки');
        }
      }
    });
  };

  const handleSetDiscount = (request: LinkRequest) => {
    setSelectedRequest(request);
    discountForm.setFieldsValue({
      discountPercent: request.discountPercent || 0
    });
    setDiscountModalVisible(true);
  };

  const handleSaveDiscount = async (values: { discountPercent: number }) => {
    if (!selectedRequest) return;
    
    try {
      await linkRequestsAPI.setDiscount(selectedRequest.id, values.discountPercent || null);
      message.success('Скидка обновлена');
      setDiscountModalVisible(false);
      discountForm.resetFields();
      setSelectedRequest(null);
      loadMyRequests();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка обновления скидки');
    }
  };

  const handleToggleEmergencyPermission = async (requestId: number, allow: boolean) => {
    try {
      setEmergencyPermissionLoading(requestId);
      await linkRequestsAPI.setEmergencyPermission(requestId, allow);
      message.success(allow ? 'Внеплановые заказы разрешены' : 'Внеплановые заказы запрещены');
      loadMyRequests();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Не удалось обновить настройку');
    } finally {
      setEmergencyPermissionLoading(null);
    }
  };

  const getStatusTag = (status: string) => {
    const configs: { [key: string]: { color: string; icon: React.ReactNode; text: string } } = {
      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Ожидает' },
      approved: { color: 'green', icon: <CheckCircleOutlined />, text: 'Одобрена' },
      rejected: { color: 'red', icon: <CloseCircleOutlined />, text: 'Отклонена' },
      archived: { color: 'gray', icon: <CloseCircleOutlined />, text: 'В архиве' }
    };
    const config = configs[status] || configs.pending;
    
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Функция для получения уникальных заявок (убирает дубли)
  const getUniqueRequests = (requests: LinkRequest[]) => {
    const uniqueMap = new Map<number, LinkRequest>();
    
    requests.forEach(request => {
      const customerId = request.customerId;
      const existing = uniqueMap.get(customerId);
      
      if (!existing) {
        uniqueMap.set(customerId, request);
      } else {
        // Приоритет: approved > pending > rejected > archived
        const priority = { approved: 4, pending: 3, rejected: 2, archived: 1 };
        if (priority[request.status as keyof typeof priority] > priority[existing.status as keyof typeof priority]) {
          uniqueMap.set(customerId, request);
        }
      }
    });
    
    return Array.from(uniqueMap.values());
  };

  const handleViewCustomer = (customer: Customer | undefined) => {
    if (!customer) {
      message.error('Информация о заказчике недоступна');
      return;
    }
    
    const data = getCustomerData(customer);
    
    Modal.info({
      title: 'Информация о заказчике',
      content: (
        <div>
          <p><strong>Компания:</strong> {data.companyName}</p>
          <p><strong>ИНН:</strong> {data.inn}</p>
          <p><strong>Контактное лицо:</strong> {data.fullName}</p>
          <p><strong>Телефон:</strong> {data.phone}</p>
          <p><strong>Статус:</strong> {customer.isApproved ? 'Одобрен' : 'Ожидает одобрения'}</p>
          <p><strong>Email:</strong> {data.email}</p>
        </div>
      ),
      width: 600,
    });
  };

  const handleDisconnectRequest = async (requestId: number) => {
    Modal.confirm({
      title: 'Разорвать связь',
      content: 'Вы уверены, что хотите разорвать связь с этим заказчиком? Заказчик будет перемещен в архив.',
      okText: 'Разорвать',
      cancelText: 'Отмена',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          await linkRequestsAPI.archiveRequest(requestId);
          message.success('Связь с заказчиком разорвана. Заказчик перемещен в архив.');
          loadMyRequests();
        } catch (error) {
          console.error('Error disconnecting request:', error);
          message.error('Ошибка при разрыве связи');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Название компании',
      key: 'companyName',
      width: 220,
      render: (text: string, record: LinkRequest) => {
        const data = getCustomerData(record.customer);
        return (
          <Button
            type="link"
            onClick={() => handleViewCustomer(record.customer)}
            style={{ padding: 0, height: 'auto', fontWeight: 500, maxWidth: '100%' }}
          >
            <span
              style={{
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'bottom'
              }}
              title={data.companyName}
            >
              {data.companyName}
            </span>
          </Button>
        );
      },
    },
    {
      title: 'ИНН',
      key: 'inn',
      width: 140,
      render: (text: string, record: LinkRequest) => {
        const data = getCustomerData(record.customer);
        return (
          <Button 
            type="link" 
            onClick={() => handleViewCustomer(record.customer)}
            style={{ padding: 0, height: 'auto' }}
          >
            <Text code>{data.inn}</Text>
          </Button>
        );
      },
    },
    {
      title: 'Контакт',
      key: 'contact',
      width: 220,
      render: (record: LinkRequest) => {
        const data = getCustomerData(record.customer);
        const displayName =
          data.fullName && data.fullName !== 'Не указано'
            ? data.fullName
            : 'ФИО не указано';
        const displayPosition =
          data.position && data.position !== 'Должность не указана'
            ? data.position
            : null;
        const displayPhone =
          data.phone && data.phone !== 'Не указан'
            ? `Телефон: ${data.phone}`
            : 'Телефон не указан';
        return (
          <div style={{ maxWidth: '100%' }}>
            <div
              style={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={displayName}
            >
              {displayName}
            </div>
            {displayPosition && (
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }}
                title={displayPosition}
              >
                {displayPosition}
              </Text>
            )}
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}
              title={displayPhone}
            >
              {displayPhone}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Email',
      key: 'email',
      width: 200,
      render: (record: LinkRequest) => {
        const data = getCustomerData(record.customer);
        return (
          <Text
            type="secondary"
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={data.email}
          >
            {data.email}
          </Text>
        );
      },
    },
    {
      title: 'Внеплановые заказы',
      key: 'emergency',
      width: 200,
      render: (record: LinkRequest) => (
        <Space direction="vertical" size={4}>
          <Tooltip title="Разрешить заказчику включать внеплановый заказ">
            <Switch
              checked={!!record.allowEmergencyOrders}
              disabled={record.status !== 'approved'}
              loading={emergencyPermissionLoading === record.id}
              onChange={(checked) => handleToggleEmergencyPermission(record.id, checked)}
            />
          </Tooltip>
          <Tag color={record.allowEmergencyOrders ? 'green' : 'default'}>
            {record.allowEmergencyOrders ? 'Разрешено' : 'Запрещено'}
          </Tag>
        </Space>
      )
    },
    {
      title: 'Инициатор',
      key: 'initiator',
      width: 120,
      render: (record: LinkRequest) => (
        <Tag color={record.initiatorType === 'supplier' ? 'blue' : 'green'}>
          {record.initiatorType === 'supplier' ? 'Вы предложили' : 'Заказчик запросил'}
        </Tag>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Скидка',
      key: 'discount',
      width: 140,
      render: (record: LinkRequest) => (
        <Space size={4}>
          {record.discountPercent ? (
            <Tag color="green" icon={<PercentageOutlined />}>
              {record.discountPercent}%
            </Tag>
          ) : (
            <Tag color="default">Нет скидки</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Дата заявки',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (record: LinkRequest) => {
        const actions = [];
        
        // Показываем кнопки только для входящих заявок (от заказчика) в статусе pending
        if (record.initiatorType === 'customer' && record.status === 'pending') {
          actions.push(
            <Button
              key="approve"
              type="primary" 
              size="small" 
              icon={<CheckCircleOutlined />}
              onClick={() => handleApproveRequest(record.id)}
              style={{ whiteSpace: 'nowrap' }}
            >
              Одобрить
            </Button>
          );
          actions.push(
            <Button
              key="reject"
              danger 
              size="small" 
              icon={<CloseCircleOutlined />}
              onClick={() => handleRejectRequest(record.id)}
              style={{ whiteSpace: 'nowrap' }}
            >
              Отклонить
            </Button>
          );
        }
        
        // Показываем кнопки для одобренных заявок
        if (record.status === 'approved') {
          actions.push(
            <Button
              key="discount"
              size="small" 
              icon={<PercentageOutlined />}
              onClick={() => handleSetDiscount(record)}
              style={{ whiteSpace: 'nowrap' }}
            >
              Скидка
            </Button>
          );
          actions.push(
            <Button
              key="disconnect"
              danger
              size="small" 
              icon={<CloseCircleOutlined />}
              onClick={() => handleDisconnectRequest(record.id)}
              style={{ whiteSpace: 'nowrap' }}
            >
              Разорвать связь
            </Button>
          );
        }
        
        return actions.length > 0 ? <Space size="small" wrap>{actions}</Space> : null;
      },
    },
  ];

  const filteredRequests = getUniqueRequests(myRequests.filter(r => r.status === tab));

  return (
    <>
      <Card
        title="Мои заказчики"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setSearchModalVisible(true)}
          >
            Найти заказчика
          </Button>
        }
      >
        <Tabs
          activeKey={tab}
          onChange={(key) => setTab(key as any)}
          items={[
            {
              key: 'approved',
              label: `Активные (${myRequests.filter(r => r.status === 'approved').length})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredRequests}
                  loading={loading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={isMobile ? { x: 'max-content' } : { x: 1200 }}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
            {
              key: 'pending',
              label: `Ожидают (${myRequests.filter(r => r.status === 'pending').length})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredRequests}
                  loading={loading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={isMobile ? { x: 'max-content' } : { x: 1200 }}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
            {
              key: 'rejected',
              label: `Отклоненные (${myRequests.filter(r => r.status === 'rejected').length})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredRequests}
                  loading={loading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={isMobile ? { x: 'max-content' } : { x: 1200 }}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
            {
              key: 'archived',
              label: `Архив (${myRequests.filter(r => r.status === 'archived').length})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={filteredRequests}
                  loading={loading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  scroll={isMobile ? { x: 'max-content' } : { x: 1200 }}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            }
          ]}
        />
      </Card>

      <Modal
        title="Найти заказчика"
        open={searchModalVisible}
        onCancel={() => {
          setSearchModalVisible(false);
          form.resetFields();
          setSearchResults([]);
        }}
        footer={null}
        width={isMobile ? '100%' : 900}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : { top: 20 }}
      >
        <div style={{ maxHeight: '80vh', overflow: 'auto' }}>
          {/* Форма поиска */}
          <Card 
            size="small" 
            title="Критерии поиска" 
            style={{ marginBottom: 16 }}
            bodyStyle={{ padding: '16px' }}
          >
            <Form 
              form={form} 
              onFinish={handleSearch} 
              layout="vertical"
              onValuesChange={(changedValues, allValues) => {
                console.log('📝 Form values changed:', changedValues);
                console.log('📝 All form values:', allValues);
              }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="inn"
                    label="ИНН заказчика"
                    rules={[
                      {
                        validator: (_, value) => {
                          console.log('🔍 INN field validator called with value:', value);
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Input 
                      placeholder="10 или 12 цифр" 
                      onChange={(e) => {
                        console.log('🔍 INN input changed:', e.target.value);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="companyName"
                    label="Название компании"
                    rules={[
                      {
                        validator: (_, value) => {
                          console.log('🔍 Company name field validator called with value:', value);
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Input 
                      placeholder="Введите название компании" 
                      onChange={(e) => {
                        console.log('🔍 Company name input changed:', e.target.value);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SearchOutlined />} 
                  loading={searching}
                  size="large"
                  onClick={() => {
                    console.log('🔍 Search button clicked');
                    const formValues = form.getFieldsValue();
                    console.log('🔍 Form values on button click:', formValues);
                  }}
                >
                  Найти заказчиков
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* Форма сообщения */}
          <Card 
            size="small" 
            title="Сообщение заказчику" 
            style={{ marginBottom: 16 }}
            bodyStyle={{ padding: '16px' }}
          >
            <Form layout="vertical">
              <Form.Item name="message" label="Текст сообщения (необязательно)">
                <Input.TextArea 
                  rows={3} 
                  placeholder="Краткое предложение или условия сотрудничества"
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Form>
          </Card>

          {/* Результаты поиска */}
          {(() => {
            console.log('🔍 Rendering search results:', searchResults.length, searchResults);
            return null;
          })()}
          
          {searchResults.length > 0 && (
            <Card 
              size="small" 
              title={`Найдено заказчиков: ${searchResults.length}`}
              bodyStyle={{ padding: '16px' }}
            >
              <Table
                dataSource={searchResults}
                rowKey="id"
                pagination={false}
                scroll={{ x: 600 }}
                size="small"
                columns={[
                  {
                    title: 'Компания',
                    key: 'companyName',
                    width: 200,
                    ellipsis: true,
                    render: (record: Customer) => (
                      <div style={{ fontWeight: 500 }}>
                        {record.user.companyName}
                      </div>
                    ),
                  },
                  {
                    title: 'ИНН',
                    key: 'inn',
                    width: 120,
                    render: (record: Customer) => (
                      <Text code>{record.user.inn}</Text>
                    ),
                  },
                  {
                    title: 'Контакт',
                    key: 'contact',
                    width: 180,
                    ellipsis: true,
                    render: (record: Customer) => (
                      <div>
                        <div style={{ fontWeight: 500 }}>{record.user.fullName || 'Не указано'}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {record.user.phone || 'Телефон не указан'}
                        </Text>
                      </div>
                    ),
                  },
                  {
                    title: 'Email',
                    key: 'email',
                    width: 200,
                    ellipsis: true,
                    render: (record: Customer) => (
                      <Text type="secondary">{record.user.email}</Text>
                    ),
                  },
                  {
                    title: 'Действия',
                    key: 'actions',
                    width: 180,
                    render: (record: Customer) => {
                      const alreadyRequested = myRequests.some(r => r.customerId === record.id);
                      
                      return (
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleSendRequest(record.id)}
                          disabled={alreadyRequested}
                          block
                        >
                          {alreadyRequested ? 'Предложение отправлено' : 'Отправить предложение'}
                        </Button>
                      );
                    },
                  },
                ]}
              />
            </Card>
          )}

          {searchResults.length === 0 && !searching && (
            <Card 
              size="small" 
              style={{ textAlign: 'center', color: '#999' }}
              bodyStyle={{ padding: '40px 16px' }}
            >
              <SearchOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <div>Введите критерии поиска и нажмите "Найти заказчиков"</div>
            </Card>
          )}
        </div>
      </Modal>

      <Modal
        title="Управление скидкой"
        open={discountModalVisible}
        onCancel={() => {
          setDiscountModalVisible(false);
          discountForm.resetFields();
          setSelectedRequest(null);
        }}
        onOk={() => discountForm.submit()}
        width={400}
      >
        {selectedRequest && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Заказчик: {getCustomerData(selectedRequest.customer).companyName}</Text>
            <br />
            <Text type="secondary">Текущая скидка: {selectedRequest.discountPercent || 0}%</Text>
          </div>
        )}
        
        <Form
          form={discountForm}
          onFinish={handleSaveDiscount}
          layout="vertical"
        >
          <Form.Item
            name="discountPercent"
            label="Скидка (%)"
            rules={[
              { type: 'number', min: 0, max: 100, message: 'Скидка должна быть от 0 до 100%' }
            ]}
          >
            <InputNumber
              min={0}
              max={100}
              step={1}
              style={{ width: '100%' }}
              placeholder="Введите размер скидки"
              addonAfter="%"
            />
          </Form.Item>
          
          <div style={{ 
            background: '#f6ffed', 
            border: '1px solid #b7eb8f',
            borderRadius: 4,
            padding: 12,
            marginBottom: 16
          }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              💡 Скидка будет применена ко всем ценам в меню для этого заказчика. 
              Оставьте поле пустым или введите 0, чтобы убрать скидку.
            </Text>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default MyCustomers;